import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useHotelStaticByJoyPlaceQuery } from '../../routes/HotelPDP3/HotelPDP3.graphql.generated';
import { JOY_BRAND_NAME } from '@shared/utils/constants';
import { buildCanonicalUrl, buildDateContext, buildHotelOgDescription, buildHotelOgImageUrl, buildHotelOgTitle } from '@apps/stays/utils/hotelOgContent';

type StaysPdpHeadProps = Readonly<{
  joyPlaceId: string;
  eventId: string | null;
  checkIn: string | null;
  checkOut: string | null;
}>;

/**
 * SSR-safe head component for /stays/:joyPlaceId.
 *
 * Fetches static hotel catalog data during SSR (ssr: true) and renders Open Graph
 * + Twitter Card meta tags via react-helmet-async so link preview crawlers
 * (Slack, iMessage, WhatsApp, etc.) can display a rich unfurl.
 *
 * The rest of the PDP body stays client-only (NoSsr / EventHandleFetcher),
 * matching the GuestSiteHeadCsr pattern used by guest site pages.
 */
export const StaysPdpHead: React.FC<StaysPdpHeadProps> = ({ joyPlaceId, eventId, checkIn, checkOut }) => {
  const { data } = useHotelStaticByJoyPlaceQuery({
    variables: { idObject: { joyPlaceId } },
    batchMode: 'fast',
    fetchPolicy: 'cache-first',
    ssr: true,
  });

  const property = data?.accommodationCatalogPropertyById;

  // All content derivation lives in hotelOgContent.ts, shared with the Share
  // Your Stay unfurl mock so the preview matches these tags.
  const ogData = useMemo(() => {
    if (!property) return null;

    const dateContext = buildDateContext(checkIn, checkOut);

    return {
      // "Hotel Name – City, ST · Jul 17–19, 2026"
      title: buildHotelOgTitle(property, dateContext?.label),
      description: buildHotelOgDescription(property),
      imageUrl: buildHotelOgImageUrl(property),
      ogUrl: buildCanonicalUrl(joyPlaceId, eventId, dateContext?.checkIn ?? null, dateContext?.checkOut ?? null),
    };
  }, [property, joyPlaceId, eventId, checkIn, checkOut]);

  if (!ogData) {
    return null;
  }

  return (
    <Helmet title={`${ogData.title} | ${JOY_BRAND_NAME}`}>
      <meta
        property="og:title"
        content={ogData.title}
      />
      <meta
        property="og:site_name"
        content={JOY_BRAND_NAME}
      />
      {ogData.description && (
        <meta
          property="og:description"
          content={ogData.description}
        />
      )}
      <meta
        property="og:url"
        content={ogData.ogUrl}
      />
      <meta
        property="og:image"
        content={ogData.imageUrl}
      />
      <meta
        property="og:type"
        content="website"
      />
      <meta
        name="twitter:card"
        content="summary_large_image"
      />
      <meta
        name="twitter:title"
        content={ogData.title}
      />
      {ogData.description && (
        <meta
          name="twitter:description"
          content={ogData.description}
        />
      )}
      <meta
        name="twitter:image"
        content={ogData.imageUrl}
      />
      <meta
        name="twitter:site"
        content="@JoytheApp"
      />
    </Helmet>
  );
};
